import { useState, useEffect } from "react";
import { 
  Gift, 
  Users, 
  DollarSign, 
  Check,
  Clock,
  Plus,
  Search,
  Award,
  Settings,
  Save,
  ToggleLeft,
  ToggleRight,
  Pencil
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReferralsPanel = ({ token }) => {
  const [referrals, setReferrals] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, eligible: 0, issued: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("referrals"); // referrals | settings
  const [newReferral, setNewReferral] = useState({
    referrer_name: "",
    referrer_email: "",
    referred_name: "",
    referred_email: ""
  });
  
  // Referral Settings State
  const [settings, setSettings] = useState({
    reward_type: "cash",
    reward_amount: 100,
    reward_description: "£100 Cash Reward",
    custom_reward_text: "",
    enabled: true,
    terms_conditions: "Reward issued when referred customer books a job and pays their deposit."
  });
  const [savingSettings, setSavingSettings] = useState(false);
  
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchReferrals();
    fetchSettings();
  }, []);

  const fetchReferrals = async () => {
    try {
      const res = await axios.get(`${API}/owner/referrals`, { headers });
      setReferrals(res.data.referrals || []);
      setCounts(res.data.counts || { pending: 0, eligible: 0, issued: 0, total: 0 });
    } catch (err) {
      console.error("Error fetching referrals:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/owner/referral-settings`, { headers });
      if (res.data) {
        setSettings({
          reward_type: res.data.reward_type || "cash",
          reward_amount: res.data.reward_amount || 100,
          reward_description: res.data.reward_description || "£100 Cash Reward",
          custom_reward_text: res.data.custom_reward_text || "",
          enabled: res.data.enabled !== false,
          terms_conditions: res.data.terms_conditions || "Reward issued when referred customer books a job and pays their deposit."
        });
      }
    } catch (err) {
      console.error("Error fetching referral settings:", err);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/owner/referral-settings`, settings, { headers });
      toast.success("Referral settings saved");
    } catch (err) {
      toast.error("Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const createReferral = async () => {
    if (!newReferral.referrer_name || !newReferral.referrer_email || 
        !newReferral.referred_name || !newReferral.referred_email) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      await axios.post(`${API}/referrals/create`, newReferral, { headers });
      toast.success("Referral created");
      setShowForm(false);
      setNewReferral({ referrer_name: "", referrer_email: "", referred_name: "", referred_email: "" });
      fetchReferrals();
    } catch (err) {
      toast.error("Failed to create referral");
    }
  };

  const updateStatus = async (referralId, status) => {
    try {
      await axios.put(`${API}/owner/referrals/${referralId}/status?status=${status}`, {}, { headers });
      toast.success(`Referral marked as ${status.replace(/_/g, " ")}`);
      fetchReferrals();
    } catch (err) {
      toast.error("Failed to update referral");
    }
  };

  const filteredReferrals = referrals.filter(r => {
    if (filter !== "all" && r.status !== filter) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        r.referrer_name?.toLowerCase().includes(search) ||
        r.referrer_email?.toLowerCase().includes(search) ||
        r.referred_name?.toLowerCase().includes(search) ||
        r.referred_email?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const statusColors = {
    pending: { bg: "bg-gray-100", text: "text-gray-700", label: "Pending" },
    deposit_paid: { bg: "bg-green-100", text: "text-green-700", label: "Reward Eligible" },
    reward_issued: { bg: "bg-blue-100", text: "text-blue-700", label: "Reward Issued" }
  };

  const totalRewardsIssued = referrals
    .filter(r => r.status === "reward_issued")
    .reduce((sum, r) => sum + (r.reward_amount || 100), 0);

  const totalRewardsPending = referrals
    .filter(r => r.status === "deposit_paid")
    .reduce((sum, r) => sum + (r.reward_amount || 100), 0);

  // Settings Tab Content
  const SettingsTab = () => (
    <div className="space-y-6">
      {/* Enable/Disable Toggle */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">Referral Program Status</h3>
            <p className="text-sm text-gray-500">Enable or disable the referral program</p>
          </div>
          <button
            onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
            className={`p-2 rounded-lg transition-colors ${settings.enabled ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-400"}`}
            data-testid="toggle-referral-program"
          >
            {settings.enabled ? (
              <ToggleRight className="w-8 h-8" />
            ) : (
              <ToggleLeft className="w-8 h-8" />
            )}
          </button>
        </div>
        <p className={`mt-2 text-sm font-medium ${settings.enabled ? "text-green-600" : "text-gray-500"}`}>
          {settings.enabled ? "Program is ACTIVE" : "Program is DISABLED"}
        </p>
      </div>

      {/* Reward Configuration */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Gift className="w-5 h-5 text-primary" />
          Reward Configuration
        </h3>
        
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <Label htmlFor="reward-amount">Reward Amount (£)</Label>
            <Input
              id="reward-amount"
              type="number"
              min="0"
              step="1"
              value={settings.reward_amount}
              onChange={(e) => setSettings({ ...settings, reward_amount: parseFloat(e.target.value) || 0 })}
              className="mt-1"
              data-testid="reward-amount-input"
            />
            <p className="text-xs text-gray-500 mt-1">Amount for cash/discount rewards</p>
          </div>
          
          <div>
            <Label htmlFor="reward-type">Reward Type</Label>
            <Input
              id="reward-type"
              type="text"
              value={settings.reward_type}
              onChange={(e) => setSettings({ ...settings, reward_type: e.target.value })}
              placeholder="e.g., cash, voucher, discount, gift card..."
              className="mt-1"
              data-testid="reward-type-input"
            />
            <p className="text-xs text-gray-500 mt-1">Free text: cash, voucher, discount, etc.</p>
          </div>
        </div>

        <div className="mb-4">
          <Label htmlFor="reward-description">Reward Description</Label>
          <Input
            id="reward-description"
            type="text"
            value={settings.reward_description}
            onChange={(e) => setSettings({ ...settings, reward_description: e.target.value })}
            placeholder="e.g., £100 Cash Reward, £50 Amazon Voucher..."
            className="mt-1"
            data-testid="reward-description-input"
          />
          <p className="text-xs text-gray-500 mt-1">This is what customers will see</p>
        </div>

        <div>
          <Label htmlFor="custom-reward">Custom Reward Text (Optional)</Label>
          <Input
            id="custom-reward"
            type="text"
            value={settings.custom_reward_text}
            onChange={(e) => setSettings({ ...settings, custom_reward_text: e.target.value })}
            placeholder="e.g., Free bathroom accessory set, 10% off next service..."
            className="mt-1"
            data-testid="custom-reward-input"
          />
          <p className="text-xs text-gray-500 mt-1">For non-monetary rewards</p>
        </div>
      </div>

      {/* Terms & Conditions */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Terms & Conditions</h3>
        <Textarea
          value={settings.terms_conditions}
          onChange={(e) => setSettings({ ...settings, terms_conditions: e.target.value })}
          placeholder="Enter the terms and conditions for the referral program..."
          rows={4}
          className="w-full"
          data-testid="terms-conditions-input"
        />
        <p className="text-xs text-gray-500 mt-1">Displayed to customers when they submit a referral</p>
      </div>

      {/* Preview */}
      <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-purple-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-3">Customer Preview</h3>
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <Gift className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="font-bold text-gray-900">{settings.reward_description || "£100 Cash Reward"}</p>
              <p className="text-sm text-gray-500">Refer a friend, earn rewards!</p>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2 italic">{settings.terms_conditions}</p>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={saveSettings} 
          disabled={savingSettings}
          className="px-8"
          data-testid="save-referral-settings-btn"
        >
          {savingSettings ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Referral Settings
            </>
          )}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="p-6" data-testid="referrals-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customer Referrals</h1>
          <p className="text-gray-600">Track referrals, manage rewards, and configure settings</p>
        </div>
        {activeTab === "referrals" && (
          <Button onClick={() => setShowForm(true)} data-testid="add-referral-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Referral
          </Button>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 border-b pb-4">
        <Button
          variant={activeTab === "referrals" ? "default" : "outline"}
          onClick={() => setActiveTab("referrals")}
          data-testid="tab-referrals"
        >
          <Users className="w-4 h-4 mr-2" />
          Referrals
        </Button>
        <Button
          variant={activeTab === "settings" ? "default" : "outline"}
          onClick={() => setActiveTab("settings")}
          data-testid="tab-settings"
        >
          <Settings className="w-4 h-4 mr-2" />
          Reward Settings
        </Button>
      </div>

      {/* Settings Tab */}
      {activeTab === "settings" && <SettingsTab />}

      {/* Referrals Tab */}
      {activeTab === "referrals" && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div 
              className="bg-white rounded-xl p-4 shadow-sm border hover:border-purple-300 hover:shadow-md transition-all cursor-pointer"
              onClick={() => setFilter("all")}
              data-testid="stat-total-referrals"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{counts.total}</p>
                  <p className="text-sm text-gray-500">Total Referrals</p>
                </div>
              </div>
            </div>

            <div 
              className="bg-white rounded-xl p-4 shadow-sm border hover:border-yellow-300 hover:shadow-md transition-all cursor-pointer"
              onClick={() => setFilter("pending")}
              data-testid="stat-pending-referrals"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{counts.pending}</p>
                  <p className="text-sm text-gray-500">Pending</p>
                </div>
              </div>
            </div>

            <div 
              className="bg-white rounded-xl p-4 shadow-sm border hover:border-green-300 hover:shadow-md transition-all cursor-pointer"
              onClick={() => setFilter("deposit_paid")}
              data-testid="stat-eligible-referrals"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Gift className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{counts.eligible}</p>
                  <p className="text-sm text-gray-500">Rewards Due</p>
                </div>
              </div>
            </div>

            <div 
              className="bg-white rounded-xl p-4 shadow-sm border hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
              onClick={() => setFilter("reward_issued")}
              data-testid="stat-issued-referrals"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <DollarSign className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">£{totalRewardsIssued}</p>
                  <p className="text-sm text-gray-500">Rewards Issued</p>
                </div>
              </div>
            </div>
          </div>

          {/* Current Reward Info Banner */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4 mb-6 border border-purple-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Award className="w-6 h-6 text-purple-600" />
                <div>
                  <p className="font-medium text-purple-900">Current Reward: {settings.reward_description}</p>
                  <p className="text-sm text-purple-700">Type: {settings.reward_type} | Status: {settings.enabled ? "Active" : "Disabled"}</p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setActiveTab("settings")}
                data-testid="edit-reward-settings-btn"
              >
                <Pencil className="w-4 h-4 mr-1" />
                Edit
              </Button>
            </div>
          </div>

          {/* Rewards Due Alert */}
          {counts.eligible > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Award className="w-6 h-6 text-green-600" />
                <div>
                  <p className="font-medium text-green-800">
                    {counts.eligible} reward{counts.eligible > 1 ? "s" : ""} ready to issue
                  </p>
                  <p className="text-sm text-green-600">
                    Total: £{totalRewardsPending} in pending rewards
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Add Referral Form */}
          {showForm && (
            <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
              <h3 className="font-semibold text-gray-900 mb-4">Add New Referral</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Referrer Name</Label>
                  <Input
                    value={newReferral.referrer_name}
                    onChange={(e) => setNewReferral({ ...newReferral, referrer_name: e.target.value })}
                    placeholder="Existing customer name"
                    className="mt-1"
                    data-testid="referrer-name-input"
                  />
                </div>
                <div>
                  <Label>Referrer Email</Label>
                  <Input
                    type="email"
                    value={newReferral.referrer_email}
                    onChange={(e) => setNewReferral({ ...newReferral, referrer_email: e.target.value })}
                    placeholder="existing@customer.com"
                    className="mt-1"
                    data-testid="referrer-email-input"
                  />
                </div>
                <div>
                  <Label>Referred Customer Name</Label>
                  <Input
                    value={newReferral.referred_name}
                    onChange={(e) => setNewReferral({ ...newReferral, referred_name: e.target.value })}
                    placeholder="New customer name"
                    className="mt-1"
                    data-testid="referred-name-input"
                  />
                </div>
                <div>
                  <Label>Referred Customer Email</Label>
                  <Input
                    type="email"
                    value={newReferral.referred_email}
                    onChange={(e) => setNewReferral({ ...newReferral, referred_email: e.target.value })}
                    placeholder="new@customer.com"
                    className="mt-1"
                    data-testid="referred-email-input"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={createReferral} data-testid="create-referral-btn">Create Referral</Button>
                <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {/* Filter & Search */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search referrals..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-referrals-input"
              />
            </div>
            <div className="flex gap-2">
              {["all", "pending", "deposit_paid", "reward_issued"].map((status) => (
                <Button
                  key={status}
                  variant={filter === status ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilter(status)}
                  data-testid={`filter-${status}`}
                >
                  {status === "all" ? "All" : 
                   status === "deposit_paid" ? "Eligible" :
                   status === "reward_issued" ? "Issued" : "Pending"}
                </Button>
              ))}
            </div>
          </div>

          {/* Referrals List */}
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            {filteredReferrals.length === 0 ? (
              <div className="p-12 text-center">
                <Gift className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600">No referrals found</p>
                <p className="text-sm text-gray-400">Create a referral to start tracking</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredReferrals.map((referral) => {
                  const statusInfo = statusColors[referral.status] || statusColors.pending;
                  
                  return (
                    <div 
                      key={referral.id} 
                      className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                      data-testid={`referral-item-${referral.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.bg} ${statusInfo.text}`}>
                              {statusInfo.label}
                            </span>
                            <span className="text-xs text-gray-400">
                              Code: {referral.code}
                            </span>
                          </div>
                          
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Referrer</p>
                              <p className="font-medium text-gray-900">{referral.referrer_name}</p>
                              <p className="text-sm text-gray-500">{referral.referrer_email}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Referred Customer</p>
                              <p className="font-medium text-gray-900">{referral.referred_name}</p>
                              <p className="text-sm text-gray-500">{referral.referred_email}</p>
                            </div>
                          </div>
                          
                          <p className="text-xs text-gray-400 mt-2">
                            Created: {new Date(referral.created_at).toLocaleDateString()}
                            {referral.issued_at && ` • Issued: ${new Date(referral.issued_at).toLocaleDateString()}`}
                          </p>
                        </div>
                        
                        <div className="text-right ml-4">
                          <p className="text-lg font-bold text-green-600">
                            £{referral.reward_amount || settings.reward_amount || 100}
                          </p>
                          <p className="text-xs text-gray-500">{referral.reward_type || settings.reward_type || "Reward"}</p>
                          
                          {/* Action Buttons */}
                          <div className="mt-2 space-y-1">
                            {referral.status === "pending" && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateStatus(referral.id, "deposit_paid");
                                }}
                                className="w-full text-xs"
                                data-testid={`mark-deposit-paid-${referral.id}`}
                              >
                                Mark Deposit Paid
                              </Button>
                            )}
                            {referral.status === "deposit_paid" && (
                              <Button
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateStatus(referral.id, "reward_issued");
                                }}
                                className="w-full text-xs bg-green-600 hover:bg-green-700"
                                data-testid={`issue-reward-${referral.id}`}
                              >
                                <Gift className="w-3 h-3 mr-1" />
                                Issue Reward
                              </Button>
                            )}
                            {referral.status === "reward_issued" && (
                              <div className="flex items-center gap-1 justify-end text-green-600">
                                <Check className="w-4 h-4" />
                                <span className="text-xs font-medium">Complete</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* How it Works */}
          <div className="mt-6 bg-blue-50 rounded-xl p-6">
            <h3 className="font-semibold text-blue-900 mb-3">How the Referral Program Works</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold shrink-0">
                  1
                </div>
                <div>
                  <p className="font-medium text-blue-900">Customer Refers Friend</p>
                  <p className="text-sm text-blue-700">Existing customer shares referral</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold shrink-0">
                  2
                </div>
                <div>
                  <p className="font-medium text-blue-900">Friend Books & Pays Deposit</p>
                  <p className="text-sm text-blue-700">Referral becomes eligible for reward</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold shrink-0">
                  3
                </div>
                <div>
                  <p className="font-medium text-blue-900">Issue {settings.reward_description}</p>
                  <p className="text-sm text-blue-700">Mark as issued once paid to referrer</p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ReferralsPanel;
